import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Printer,
  AlertCircle,
  Pencil,
  X,
  Camera,
  Plus,
  Tag,
  ListChecks,
  Search,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect, useCallback } from "react";
import { useOrders, useCancelOrder, useOrderLabels } from "@/api/orders";
import { Order, OrderStatus } from "@/api.types";
import { EditOrderForm } from "./edit-order-form";
import { CreateOrderButton } from "./create-order-button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useTags,
  useOrderTags,
  useAddTagToOrder,
  useRemoveTagFromOrder,
} from "@/api/tags";
import { CreateTaskDialog } from "../tasks/create-task-dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { type CarouselApi } from "@/components/ui/carousel";
import { Input } from "@/components/ui/input";
import { debounce } from "lodash";

const statusColors: Record<string, string> = {
  REGISTERED: "bg-blue-500",
  PENDING: "bg-gray-500",
  PICKUP_PENDING: "bg-yellow-500",
  OUT_FOR_PICKUP: "bg-blue-500",
  PICKUP_ASSIGNED: "bg-blue-500",
  PICKED_UP: "bg-indigo-700",
  DEPOT_RECEIVE_PENDING: "bg-purple-500",
  DEPOT_RECEIVE_IN_PROGRESS: "bg-purple-500",
  AT_DEPOT: "bg-purple-500",
  DEPOT_SORT_PENDING: "bg-violet-500",
  DEPOT_SORT_IN_PROGRESS: "bg-violet-500",
  SORTED: "bg-violet-500",
  DEPOT_DISPATCH_PENDING: "bg-cyan-500",
  DEPOT_DISPATCH_IN_PROGRESS: "bg-cyan-500",
  IN_TRANSIT: "bg-cyan-500",
  DELIVERY_PENDING: "bg-yellow-500",
  OUT_FOR_DELIVERY: "bg-yellow-500",
  DELIVERED: "bg-green-700",
  RETURN_PENDING: "bg-orange-500",
  RETURN_IN_PROGRESS: "bg-orange-500",
  RETURNED: "bg-orange-500",
  PICKUP_FAILED: "bg-red-500",
  DEPOT_RECEIVE_FAILED: "bg-red-500",
  DEPOT_SORT_FAILED: "bg-red-500",
  DEPOT_DISPATCH_FAILED: "bg-red-500",
  DELIVERY_FAILED: "bg-red-500",
  RETURN_FAILED: "bg-red-500",
  FAILED: "bg-red-500",
  PICKUP_CANCELLED: "bg-red-700",
  DEPOT_RECEIVE_CANCELLED: "bg-red-700",
  DEPOT_SORT_CANCELLED: "bg-red-700",
  DEPOT_DISPATCH_CANCELLED: "bg-red-700",
  DELIVERY_CANCELLED: "bg-red-700",
  RETURN_CANCELLED: "bg-red-700",
  CANCELLED: "bg-red-700",
  PARTIALLY_PICKED_UP: "bg-indigo-500",
  PARTIALLY_DELIVERED: "bg-green-500",
};

const ORDER_STATUSES = {
  [OrderStatus.REGISTERED]: "Registered",
  [OrderStatus.PICKUP_PENDING]: "Pickup Pending",
  [OrderStatus.OUT_FOR_PICKUP]: "Out for Pickup",
  [OrderStatus.PICKUP_ASSIGNED]: "Pickup Assigned",
  [OrderStatus.PICKED_UP]: "Picked Up",
  [OrderStatus.DEPOT_RECEIVE_PENDING]: "Depot Receive Pending",
  [OrderStatus.DEPOT_RECEIVE_IN_PROGRESS]: "Depot Receive In Progress",
  [OrderStatus.AT_DEPOT]: "At Depot",
  [OrderStatus.DEPOT_SORT_PENDING]: "Depot Sort Pending",
  [OrderStatus.DEPOT_SORT_IN_PROGRESS]: "Depot Sort In Progress",
  [OrderStatus.SORTED]: "Sorted",
  [OrderStatus.DEPOT_DISPATCH_PENDING]: "Depot Dispatch Pending",
  [OrderStatus.DEPOT_DISPATCH_IN_PROGRESS]: "Depot Dispatch In Progress",
  [OrderStatus.IN_TRANSIT]: "In Transit",
  [OrderStatus.DELIVERY_PENDING]: "Delivery Pending",
  [OrderStatus.OUT_FOR_DELIVERY]: "Out for Delivery",
  [OrderStatus.DELIVERED]: "Delivered",
  [OrderStatus.RETURN_PENDING]: "Return Pending",
  [OrderStatus.RETURN_IN_PROGRESS]: "Return In Progress",
  [OrderStatus.RETURNED]: "Returned",
  [OrderStatus.PICKUP_FAILED]: "Pickup Failed",
  [OrderStatus.DEPOT_RECEIVE_FAILED]: "Depot Receive Failed",
  [OrderStatus.DEPOT_SORT_FAILED]: "Depot Sort Failed",
  [OrderStatus.DEPOT_DISPATCH_FAILED]: "Depot Dispatch Failed",
  [OrderStatus.DELIVERY_FAILED]: "Delivery Failed",
  [OrderStatus.RETURN_FAILED]: "Return Failed",
  [OrderStatus.PICKUP_CANCELLED]: "Pickup Cancelled",
  [OrderStatus.DEPOT_RECEIVE_CANCELLED]: "Depot Receive Cancelled",
  [OrderStatus.DEPOT_SORT_CANCELLED]: "Depot Sort Cancelled",
  [OrderStatus.DEPOT_DISPATCH_CANCELLED]: "Depot Dispatch Cancelled",
  [OrderStatus.DELIVERY_CANCELLED]: "Delivery Cancelled",
  [OrderStatus.RETURN_CANCELLED]: "Return Cancelled",
} as const;

// Component to display and manage tags for an order
function OrderTags({ orderId }: { orderId: string }) {
  const { data: orderTags } = useOrderTags(orderId);
  const removeTagMutation = useRemoveTagFromOrder();

  const handleRemoveTag = (orderId: string, tagId: string) => {
    removeTagMutation.mutate({
      orderId,
      tagId,
    });
  };

  return (
    <div className="flex flex-wrap gap-1 max-w-[200px]">
      {orderTags?.map((orderTag) => (
        <Badge
          key={orderTag.id}
          className={`flex items-center`}
          style={{ backgroundColor: orderTag.tag.color ?? "bg-gray-500" }}
          variant="default"
        >
          {orderTag.tag.name}
          <button
            className="ml-1 rounded-full h-4 w-4 flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveTag(orderId, orderTag.tag.id);
            }}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}

// Helper type for photo metadata
interface PhotoMeta {
  url: string;
  taskType: string;
  status: string;
  time: string;
  notes?: string;
}

export function OrdersTable() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselImages, setCarouselImages] = useState<PhotoMeta[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [statusFilter, setStatusFilter] = useState<OrderStatus | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedOrderForTags, setSelectedOrderForTags] = useState<
    string | null
  >(null);
  const [selectedTagId, setSelectedTagId] = useState<string>("");
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [selectedOrderForTask, setSelectedOrderForTask] = useState<
    string | null
  >(null);
  const { data: ordersResponse, isLoading } = useOrders(
    page,
    limit,
    statusFilter ?? undefined,
    debouncedSearchTerm || undefined,
    typeFilter
  );

  const cancelOrderMutation = useCancelOrder();
  const { printLabels, printBulkLabels } = useOrderLabels();

  // Get available tags
  const { data: tagsData } = useTags({ isActive: true });

  // Add tag mutation
  const addTagMutation = useAddTagToOrder({
    onSuccess: () => {
      setTagDialogOpen(false);
      setSelectedTagId("");
    },
  });

  const handlePrintLabels = async (orderId: string) => {
    try {
      const blob = await printLabels.mutateAsync(orderId);
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error printing labels:", error);
    }
  };

  const handleBulkPrintLabels = async () => {
    try {
      const blob = await printBulkLabels.mutateAsync(selectedOrders);
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error printing bulk labels:", error);
    }
  };

  const handleHeaderCheckboxChange = (checked: boolean) => {
    setSelectedOrders(
      checked ? (ordersResponse?.data ?? []).map((order) => order.id) : []
    );
  };

  // Helper function to get all task proof images
  const getAllTaskProofImages = (order: Order): PhotoMeta[] => {
    // Get tasks from the order's tasks array
    const tasks = order.tasks ?? [];
    return tasks
      .filter(
        (task) =>
          task.proof?.mediaItems &&
          Array.isArray(task.proof.mediaItems) &&
          task.proof.mediaItems.length > 0
      )
      .flatMap((task) =>
        (task.proof?.mediaItems ?? []).map((item) => ({
          url: item.url ?? "",
          taskType: task.type ?? "UNKNOWN",
          status: task.status ?? "UNKNOWN",
          time: task.updatedAt ?? task.createdAt ?? new Date().toISOString(),
          notes: task.proof?.notes,
        }))
      );
  };

  // Handle opening the tag dialog
  const handleOpenTagDialog = (orderId: string) => {
    setSelectedOrderForTags(orderId);
    setTagDialogOpen(true);
  };

  // Handle opening the create task dialog
  const handleOpenCreateTaskDialog = (orderId: string) => {
    setSelectedOrderForTask(orderId);
    setCreateTaskDialogOpen(true);
  };

  // Handle adding a tag
  const handleAddTag = () => {
    if (selectedOrderForTags && selectedTagId) {
      addTagMutation.mutate({
        orderId: selectedOrderForTags,
        tagId: selectedTagId,
      });
    }
  };

  const handleOpenCarousel = (order: Order) => {
    // Get all proof images from tasks
    const images = getAllTaskProofImages(order);

    if (images.length === 0) {
      return; // Don't open carousel if there are no images
    }

    setCarouselImages(images);
    setCurrentImageIndex(0);
    setCarouselOpen(true);
  };

  // Set up carousel API and handle slide changes
  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    // Define the select handler
    const onSelect = () => {
      // Now that we've checked carouselApi exists, TS should infer the types
      // If errors persist, it might indicate an issue with the CarouselApi type itself
      setCurrentImageIndex(carouselApi.selectedScrollSnap());
    };

    // Register the event listener
    carouselApi.on("select", onSelect);

    // Cleanup function to remove the listener
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]); // Dependency array remains the same

  // Debounce search term
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setDebouncedSearchTerm(term);
      setPage(1); // Reset to first page on new search
    }, 300),
    []
  );

  // Effect to handle search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);

    // Cleanup function to cancel debounce on unmount
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  // Handle search form submission (for Enter key)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Immediately trigger search on form submit
    setDebouncedSearchTerm(searchTerm);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4 flex-wrap gap-4">
        <div className="flex gap-4">
          <CreateOrderButton />
          <Button
            onClick={handleBulkPrintLabels}
            disabled={selectedOrders.length === 0}
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Selected Labels ({selectedOrders.length})
          </Button>
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex relative">
            <Input
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-r-none w-[200px] pr-8"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <Button type="submit" className="rounded-l-none">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          {/* Status Filter */}
          <Select
            value={statusFilter ?? "ALL"}
            onValueChange={(value) => {
              setStatusFilter(value === "ALL" ? null : (value as OrderStatus));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              {Object.entries(ORDER_STATUSES).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select
            value={typeFilter ?? "all"}
            onValueChange={(value) => {
              setTypeFilter(value === "all" ? undefined : value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="STANDARD">Standard</SelectItem>
              <SelectItem value="EXPRESS">Express</SelectItem>
              <SelectItem value="SAME_DAY">Same Day</SelectItem>
            </SelectContent>
          </Select>

          {/* Results per page selector */}
          <Select
            value={limit.toString()}
            onValueChange={(value) => {
              setLimit(parseInt(value));
              setPage(1); // Reset to first page when changing the limit
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Per page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 per page</SelectItem>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Search indicator */}
      {debouncedSearchTerm && (
        <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
          <Search className="h-4 w-4" />
          <span>Searching for: "{debouncedSearchTerm}"</span>
          <button
            onClick={handleClearSearch}
            className="text-gray-400 hover:text-gray-600 ml-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
            <span>Loading orders...</span>
          </div>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="rounded-md border overflow-x-auto">
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={
                      (ordersResponse?.data?.length ?? 0) > 0 &&
                      selectedOrders.length ===
                        (ordersResponse?.data?.length ?? 0)
                    }
                    onCheckedChange={handleHeaderCheckboxChange}
                    disabled={!(ordersResponse?.data?.length ?? 0)}
                  />
                </TableHead>
                <TableHead className="sticky left-0 bg-white z-10 min-w-[150px]">
                  Order Details
                </TableHead>
                <TableHead className="min-w-[120px]">Status</TableHead>
                <TableHead className="min-w-[100px]">Type</TableHead>
                <TableHead className="min-w-[200px]">Placer Customer</TableHead>
                <TableHead className="min-w-[200px]">Sender</TableHead>
                <TableHead className="min-w-[200px]">Recipient</TableHead>
                <TableHead className="min-w-[150px]">Pickup Notes</TableHead>
                <TableHead className="min-w-[150px]">Delivery Notes</TableHead>
                <TableHead className="min-w-[200px]">Photos</TableHead>
                <TableHead className="min-w-[200px]">Parcels</TableHead>
                <TableHead className="min-w-[100px]">Subtotal</TableHead>
                <TableHead className="min-w-[150px]">Tags</TableHead>
                <TableHead className="min-w-[80px] sticky right-0 bg-white z-10">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(ordersResponse?.data ?? []).length > 0 ? (
                (ordersResponse?.data ?? []).map((order) => {
                  const subtotal =
                    order.appliedFees?.reduce(
                      (sum, fee) => sum + fee.amount,
                      0
                    ) ?? 0;

                  return (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedOrders.includes(order.id)}
                          onCheckedChange={(checked) => {
                            setSelectedOrders(
                              checked
                                ? [...selectedOrders, order.id]
                                : selectedOrders.filter((id) => id !== order.id)
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell className="sticky left-0 bg-white">
                        <div>
                          <p className="font-medium">#{order.orderNumber}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${statusColors[order.status]}`}
                          variant="default"
                        >
                          {order.status.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            order.type === "STANDARD"
                              ? "bg-blue-500"
                              : order.type === "EXPRESS"
                                ? "bg-orange-500"
                                : "bg-red-500"
                          }
                          variant="default"
                        >
                          {order.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {order.placerCustomer ? (
                          <div>
                            <p className="font-medium">
                              {order.placerCustomer.businessAccount
                                ?.companyName ??
                                `${order.placerCustomer.user?.firstName ?? ""} ${
                                  order.placerCustomer.user?.lastName ?? ""
                                }`}
                            </p>
                            <p className="text-sm text-gray-500">
                              {order.placerCustomer.user?.email}
                            </p>
                            <p className="text-sm text-gray-500">
                              {order.placerCustomer.user?.phone}
                            </p>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.senderName}</p>
                          <p className="text-sm text-gray-500">
                            {order.senderEmail}
                          </p>
                          <p className="text-sm text-gray-500">
                            {order.senderPhone}
                          </p>
                          <p className="text-xs text-gray-400">
                            {order.pickupAddress.line1},{" "}
                            {order.pickupAddress.city}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.recipientName}</p>
                          <p className="text-sm text-gray-500">
                            {order.recipientEmail}
                          </p>
                          <p className="text-sm text-gray-500">
                            {order.recipientPhone}
                          </p>
                          <p className="text-xs text-gray-400">
                            {order.deliveryAddress.line1},{" "}
                            {order.deliveryAddress.city}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[200px] overflow-hidden text-ellipsis">
                          {order.pickupNotes || "No notes"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[200px] overflow-hidden text-ellipsis">
                          {order.deliveryNotes || "No notes"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="min-h-[100px]">
                          {(() => {
                            const images = getAllTaskProofImages(order);
                            const totalCount = images.length;

                            return (
                              <>
                                <p className="font-medium">
                                  {totalCount} photos
                                </p>
                                <div className="flex mt-2 space-x-2">
                                  {totalCount > 0 ? (
                                    <>
                                      {images
                                        .slice(0, 3)
                                        .map((image, index) => (
                                          <div
                                            key={index}
                                            className="relative w-12 h-12 cursor-pointer"
                                            onClick={() =>
                                              handleOpenCarousel(order)
                                            }
                                          >
                                            <img
                                              src={image.url}
                                              alt={`${image.taskType} proof ${index + 1}`}
                                              className="w-full h-full object-cover rounded-md"
                                              onError={(e) => {
                                                console.error(
                                                  `Failed to load image: ${image.url}`
                                                );
                                                (
                                                  e.target as HTMLImageElement
                                                ).src =
                                                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
                                              }}
                                            />
                                          </div>
                                        ))}
                                      {totalCount > 3 && (
                                        <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center text-gray-500">
                                          +{totalCount - 3}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="flex items-center space-x-2">
                                      <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                                        <Camera className="h-6 w-6 text-gray-400" />
                                      </div>
                                      <p className="text-sm text-gray-500">
                                        No photos
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {order.parcels.length} parcels
                          </p>
                          {order.parcels.map((parcel) => (
                            <p
                              key={parcel.id}
                              className="text-sm text-gray-500"
                            >
                              {parcel.trackingNumber} ({parcel.weight}kg
                              {parcel.dimensions &&
                                ` - ${parcel.dimensions.length}x${parcel.dimensions.width}x${parcel.dimensions.height}cm`}
                              )
                            </p>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          ${subtotal.toFixed(2)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          <OrderTags orderId={order.id} />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2"
                            onClick={() => handleOpenTagDialog(order.id)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            <Tag className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="sticky right-0 bg-white z-10">
                        <div className="flex flex-col items-center space-y-1">
                          {/* Edit Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedOrderId(order.id);
                              setDialogOpen(true);
                            }}
                            title="Edit Order"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>

                          {/* Print Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrintLabels(order.id)}
                            title="Print Labels"
                          >
                            <Printer className="h-4 w-4" />
                          </Button>

                          {/* Cancel Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              cancelOrderMutation.mutate({
                                orderId: order.id,
                              })
                            }
                            className="text-red-600" // Add red color for emphasis
                            title="Cancel Order"
                            // Add disable logic if necessary, e.g., based on order status
                            // disabled={!canCancelOrder(order.status)}
                          >
                            <AlertCircle className="h-4 w-4" />
                          </Button>

                          {/* More Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleOpenTagDialog(order.id)}
                                className="cursor-pointer"
                              >
                                <Tag className="mr-2 h-4 w-4" />
                                Manage Tags
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleOpenCreateTaskDialog(order.id)
                                }
                                className="cursor-pointer"
                              >
                                <ListChecks className="mr-2 h-4 w-4" />
                                Create Task
                              </DropdownMenuItem>
                              {/* Add other actions here if needed */}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-8">
                    {debouncedSearchTerm ? (
                      <div className="flex flex-col items-center gap-2">
                        <Search className="h-8 w-8 text-gray-400" />
                        <p className="text-gray-500">
                          No orders found for "{debouncedSearchTerm}"
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearSearch}
                        >
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-gray-500">No orders found</p>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
          </DialogHeader>
          {selectedOrderId && (
            <EditOrderForm
              onSuccess={() => setDialogOpen(false)}
              orderId={selectedOrderId}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Tag Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Tag to Order</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <Select value={selectedTagId} onValueChange={setSelectedTagId}>
              <SelectTrigger>
                <SelectValue placeholder="Select tag" />
              </SelectTrigger>
              <SelectContent>
                {tagsData?.tags?.map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>
                    <div className="flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: tag.color ?? "#888888" }}
                      />
                      {tag.name}
                      {tag.category?.name && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({tag.category.name})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setTagDialogOpen(false);
                setSelectedTagId("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleAddTag}
              disabled={!selectedTagId || addTagMutation.isPending}
            >
              {addTagMutation.isPending ? "Adding..." : "Add Tag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      {createTaskDialogOpen && selectedOrderForTask && (
        <Dialog
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setCreateTaskDialogOpen(false);
              setSelectedOrderForTask(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Create Task for Order</DialogTitle>
            </DialogHeader>
            <CreateTaskDialog
              onCreated={() => {
                setCreateTaskDialogOpen(false);
                setSelectedOrderForTask(null);
              }}
              initialOrderId={selectedOrderForTask}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Image Carousel Dialog */}
      <Dialog open={carouselOpen} onOpenChange={setCarouselOpen}>
        <DialogContent className="sm:max-w-5xl p-0 overflow-hidden max-h-[90vh] w-[95vw] sm:w-auto mx-auto">
          <DialogHeader className="p-4 border-b bg-gray-50 relative">
            <div className="flex items-center justify-between pr-8">
              <div className="flex flex-row items-center gap-3">
                <DialogTitle className="text-xl">Order Photos</DialogTitle>
                {carouselImages.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge
                      className={
                        carouselImages[currentImageIndex]?.taskType === "PICKUP"
                          ? "bg-indigo-600"
                          : carouselImages[currentImageIndex]?.taskType ===
                              "DELIVERY"
                            ? "bg-green-600"
                            : "bg-gray-500"
                      }
                    >
                      {carouselImages[currentImageIndex]?.taskType}
                    </Badge>
                    <Badge
                      className={
                        statusColors[
                          carouselImages[currentImageIndex]?.status
                        ] || "bg-gray-500"
                      }
                    >
                      {carouselImages[currentImageIndex]?.status.replace(
                        /_/g,
                        " "
                      )}
                    </Badge>
                  </div>
                )}
              </div>
              {carouselImages.length > 0 && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">
                    {new Date(
                      carouselImages[currentImageIndex]?.time
                    ).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">
                    Image {currentImageIndex + 1} of {carouselImages.length}
                  </p>
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="p-6 relative">
            <Carousel
              className="w-full"
              setApi={setCarouselApi}
              opts={{
                startIndex: 0,
                align: "center",
              }}
            >
              <CarouselContent>
                {carouselImages.map((image, index) => (
                  <CarouselItem key={index}>
                    <div className="flex flex-col items-center space-y-4">
                      <div className="relative w-full max-h-[70vh] overflow-hidden rounded-lg">
                        <img
                          src={image.url}
                          alt={`${image.taskType} proof image`}
                          className="mx-auto object-contain max-h-[70vh] w-full"
                          onError={(e) => {
                            console.error(`Failed to load image: ${image.url}`);
                            (e.target as HTMLImageElement).src =
                              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
                          }}
                        />
                      </div>
                      {image.notes && (
                        <p className="text-sm bg-gray-50 p-2 rounded-md w-full">
                          Note: {image.notes}
                        </p>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Fixed position navigation buttons for better mobile visibility */}
              <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-10" />
              <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-10" />
            </Carousel>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {!isLoading && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-gray-500">
            {debouncedSearchTerm ? (
              <>
                Found {ordersResponse?.metadata?.total ?? 0} results for "
                {debouncedSearchTerm}"
                {ordersResponse?.metadata?.total
                  ? ` (showing ${ordersResponse?.metadata?.total ? (page - 1) * limit + 1 : 0} to ${Math.min(page * limit, ordersResponse?.metadata?.total ?? 0)})`
                  : ""}
              </>
            ) : (
              <>
                Showing{" "}
                {ordersResponse?.metadata?.total ? (page - 1) * limit + 1 : 0}{" "}
                to{" "}
                {Math.min(page * limit, ordersResponse?.metadata?.total ?? 0)}{" "}
                of {ordersResponse?.metadata?.total ?? 0} results
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= (ordersResponse?.metadata?.totalPages ?? 0)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
